import { Popover } from "@kobalte/core/popover"
import { useNavigate } from "@solidjs/router"
import { Button } from "@opencode/ui/button"
import { Avatar } from "@opencode/ui/avatar"
import { Icon } from "@opencode/ui/icon"
import { Logo } from "@opencode/ui/logo"
import { useDialog } from "@opencode/ui/context/dialog"
import { createEffect, createMemo, For, Show, type ParentProps } from "solid-js"
import { createStore } from "solid-js/store"
import { Dynamic, Portal } from "solid-js/web"
import { useLanguage } from "@/runtime/i18n/language"
import { ServerConnection, serverName, useServers } from "@/runtime/server/registry"
import { useGlobal } from "@/runtime/server/runtime"
import { useCurrentRoute, useLayout } from "@/shell/state/layout"
import { useTabs } from "@/shell/tabs/tabs"
import type { SairAccountContext } from "./account"
import { DialogServer } from "@/servers/connect/dialog"
import { canManageServers, usePlatform } from "@/runtime/platform/platform"
import { useSairEntry } from "./entry-context"
import { ExternalLink } from "@/runtime/platform/external-link"
import "./gate.css"

export function useSairTarget() {
  const servers = useServers()
  const global = useGlobal()
  const layout = useLayout()
  const tabs = useTabs()
  const route = useCurrentRoute()
  const connection = createMemo(() => {
    const current = route()
    const key =
      current.type === "session"
        ? current.server
        : current.type === "draft"
          ? tabs.store.find((tab) => tab.type === "draft" && tab.draftID === current.draftID)?.server
          : layout.home.selection().server
    return servers.list.find((server) => ServerConnection.key(server) === key) ?? servers.visible[0]
  })
  return {
    connection,
    account: createMemo(() => {
      const conn = connection()
      return conn ? global.ensureServerCtx(conn).account : undefined
    }),
  }
}

export function SairGate(props: ParentProps) {
  const target = useSairTarget()
  const entry = useSairEntry()
  createEffect(() => {
    const account = target.account()
    const status = account?.state.status
    entry.report(
      account?.authenticated()
        ? "ready"
        : !account || account.state.loadError || status?.auth === "unavailable"
          ? "recovery"
          : !status
            ? "checking"
            : status.attempt
              ? "pending"
              : "sign-in",
    )
  })
  return (
    <>
      <Show when={target.account()?.authenticated()}>{props.children}</Show>
      <Show when={entry.panel()}>
        {(panel) => (
          <Portal mount={panel()}>
            <Show when={!target.account()?.authenticated()}>
              <SairSignIn account={target.account()} />
            </Show>
          </Portal>
        )}
      </Show>
    </>
  )
}

function ServerChoice(props: { manage?: boolean }) {
  const platform = usePlatform()
  const language = useLanguage()
  const servers = useServers()
  const layout = useLayout()
  const navigate = useNavigate()
  const target = useSairTarget()
  const dialog = useDialog()
  const select = (server: ServerConnection.Http) => {
    layout.home.setSelection({ server: ServerConnection.key(server) })
    navigate("/?view=library")
  }
  return (
    <Show when={canManageServers(platform)}>
      <div class="flex flex-wrap items-center gap-2">
        <label class="flex items-center gap-2 text-v2-text-text-muted text-[12px] leading-[var(--line-height-compact)]">
          <span>{language.t("sair.server")}</span>
          <select
            class="min-w-0 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 py-1"
            aria-label={language.t("sair.server")}
            value={target.connection() ? ServerConnection.key(target.connection()!) : ""}
            onChange={(event) => {
              layout.home.setSelection({ server: ServerConnection.Key.make(event.currentTarget.value) })
              navigate("/?view=library")
            }}
          >
            <For each={servers.visible}>
              {(conn) => <option value={ServerConnection.key(conn)}>{serverName(conn)}</option>}
            </For>
          </select>
        </label>
        <Show when={props.manage}>
          <Button
            variant="neutral"
            size="small"
            onClick={() => dialog.show(() => <DialogServer mode="add" onSave={select} />)}
          >
            {language.t("sair.server.add")}
          </Button>
          <Show when={target.connection()?.type === "http"}>
            <Button
              variant="neutral"
              size="small"
              onClick={() => {
                const server = target.connection()
                if (server?.type === "http")
                  dialog.show(() => <DialogServer mode="edit" server={server} onSave={select} />)
              }}
            >
              {language.t("sair.server.edit")}
            </Button>
          </Show>
        </Show>
      </div>
    </Show>
  )
}

export function SairSignIn(props: { account?: SairAccountContext; web?: boolean; completing?: boolean }) {
  const language = useLanguage()
  const platform = usePlatform()
  const status = () => props.account?.state.status
  const pending = () => status()?.attempt
  const error = () => status()?.error?.code
  const accessDenied = () => error() === "omm_access_denied"
  const needsAction = () => error() === "email_verification_required" || error() === "identity_conflict"
  const checking = () => !!props.account && !status() && !props.account.state.loadError
  const unavailable = createMemo(
    () => !props.account || props.account.state.loadError || status()?.auth === "unavailable",
  )
  const signingOut = () => error() === "signing_out"
  const storageFailed = () => error() === "logout_storage_error" || (accessDenied() && status()?.auth === "unavailable")
  const restartSignIn = () =>
    accessDenied() ||
    error() === "expired_token" ||
    error() === "invalid_grant" ||
    error() === "invalid_token" ||
    error() === "access_denied" ||
    error() === "workspace_access_denied" ||
    error() === "authorization_cancelled"
  const pendingRetry = () => !!props.account?.state.loadError || needsAction()
  const brandHeading = () => !checking() && !unavailable() && !accessDenied() && !needsAction() && !pending()
  const notice = createMemo(() => {
    if (checking() || signingOut()) return
    if (!props.account) return "sair.connection.missing"
    if (accessDenied())
      return status()?.auth === "unavailable" ? "sair.access.cleanup" : "sair.access.denied.description"
    if (error() === "workspace_access_denied") return "sair.access.workspace.description"
    if (error() === "authorization_cancelled") return "sair.signIn.denied"
    if (error() === "logout_storage_error") return "sair.logout.storageFailed"
    if (error() === "email_verification_required") return "sair.access.email.description"
    if (error() === "identity_conflict") return "sair.access.identity.description"
    if (unavailable()) return "sair.check.failed.description"
    if (error() === "invalid_grant" || error() === "invalid_token") return "sair.signIn.sessionExpired"
    if (error() === "expired_token") return props.web ? "sair.web.expired" : "sair.signIn.expired"
    if (error() === "access_denied") return "sair.signIn.denied"
    if (error() === "sign_in_return") return "sair.web.returnInvalid"
    if (error() === "sign_in_storage") return "sair.web.storageFailed"
    if (
      props.account.state.revokeError &&
      status()?.auth === "signed_out" &&
      !pending() &&
      !props.account.state.busy &&
      !props.account.state.actionError
    )
      return "sair.logout.incomplete"
    if (props.account.state.actionError || error()) return "sair.signIn.failed"
  })
  const [copy, setCopy] = createStore({ done: false, failed: false })
  createEffect(() => {
    pending()?.attemptID
    setCopy({ done: false, failed: false })
  })
  return (
    <section class="sair-sign-in-content [app-region:no-drag]">
      <Show when={!checking()}>
        <Dynamic
          component={brandHeading() ? "h1" : "div"}
          class="sair-sign-in-wordmark"
          role={brandHeading() ? undefined : "img"}
          aria-label={language.t("sair.logo")}
        >
          <Logo />
        </Dynamic>
      </Show>
      <div class="sair-sign-in-message">
        <div class="sair-sign-in-heading">
          <Show when={!checking() && !brandHeading()}>
            <h1>
              {language.t(
                signingOut()
                  ? "sair.logout.signingOut"
                  : accessDenied()
                    ? "sair.access.denied.title"
                    : storageFailed()
                      ? "sair.logout.failed.title"
                      : needsAction()
                        ? "sair.access.action.title"
                        : unavailable()
                          ? "sair.check.failed"
                          : pending()
                            ? props.web
                              ? "sair.web.pendingTitle"
                              : "sair.signIn.pendingTitle"
                            : "sair.signIn.heading",
              )}
            </h1>
          </Show>
          <Show when={!checking() && !notice() && !pending() && !signingOut()}>
            <p>{language.t("sair.signIn.intro")}</p>
          </Show>
          <Show when={signingOut()}>
            <p role="status">{language.t("sair.logout.signingOut.description")}</p>
          </Show>
          <Show when={pending() && !unavailable()}>
            <p role="status">
              {language.t(
                needsAction()
                  ? "sair.access.paused"
                  : props.completing
                    ? "sair.web.completing"
                    : props.web
                      ? "sair.web.waiting"
                      : "sair.signIn.waiting",
              )}
            </p>
          </Show>
        </div>
        <Show when={notice()}>
          {(key) => (
            <p role="alert" class="sair-sign-in-notice">
              {language.t(key())}
            </p>
          )}
        </Show>
        <Show when={props.account?.state.loadError && notice() !== "sair.check.failed.description"}>
          <p role="alert" class="sair-sign-in-notice">
            {language.t("sair.check.failed.description")}
          </p>
        </Show>
        <Show
          when={
            props.account?.state.actionError &&
            !props.account.state.loadError &&
            notice() !== "sair.signIn.failed" &&
            (!props.web || !notice())
          }
        >
          <p role="alert" class="sair-sign-in-notice">
            {language.t("sair.signIn.failed")}
          </p>
        </Show>
      </div>
      <Show
        when={!signingOut() && !storageFailed() && pending()}
        fallback={
          <div class="sair-sign-in-actions">
            <Show when={props.account?.state.loadError && (signingOut() || storageFailed())}>
              <Button
                variant="contrast"
                size="large"
                class="sair-sign-in-primary"
                disabled={props.account?.state.busy || props.account?.state.checking}
                onClick={() => void props.account?.retry()}
              >
                {language.t("sair.retry")}
              </Button>
            </Show>
            <Show when={!checking() && !signingOut()}>
              <Show when={!unavailable() && status()?.auth === "signed_out"}>
                <Button
                  variant="contrast"
                  size="large"
                  class="sair-sign-in-primary"
                  disabled={props.account?.state.busy}
                  onClick={() => void props.account?.start()}
                >
                  <span>
                    {props.account?.state.busy
                      ? language.t(props.web ? "sair.web.opening" : "sair.signIn.opening")
                      : language.t(
                          props.web
                            ? "sair.web.continue"
                            : restartSignIn()
                              ? "sair.access.signIn"
                              : "sair.signIn.continue",
                        )}
                  </span>
                  <Icon name="arrow-right" />
                </Button>
              </Show>
              <Show when={!!props.account && unavailable() && !storageFailed()}>
                <Button
                  variant="contrast"
                  size="large"
                  class="sair-sign-in-primary"
                  disabled={props.account?.state.busy || props.account?.state.checking}
                  onClick={() => void props.account?.retry()}
                >
                  {language.t("sair.retry")}
                </Button>
              </Show>
              <Show when={storageFailed() && !props.account?.state.loadError}>
                <Button
                  variant="contrast"
                  size="large"
                  class="sair-sign-in-primary"
                  disabled={props.account?.state.busy || props.account?.state.checking}
                  onClick={() => void props.account?.logout()}
                >
                  {language.t("sair.logout.action")}
                </Button>
              </Show>
              <Show when={status()?.auth === "unavailable" && !storageFailed()}>
                <div class="sair-sign-in-secondary">
                  <Button
                    variant="ghost"
                    disabled={props.account?.state.busy}
                    onClick={() => void props.account?.logout()}
                  >
                    {language.t("sair.logout.action")}
                  </Button>
                </div>
              </Show>
            </Show>
          </div>
        }
      >
        {(attempt) => (
          <div class="sair-sign-in-actions">
            <Show when={!props.web}>
              <p class="sair-sign-in-code-label">{language.t("sair.signIn.code")}</p>
              <div class="sair-sign-in-code-row">
                <p class="sair-sign-in-code" aria-label={language.t("sair.signIn.code")}>
                  {attempt().userCode}
                </p>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => {
                    const text = attempt().userCode
                    void Promise.resolve()
                      .then(() => platform.writeClipboardText?.(text) ?? navigator.clipboard.writeText(text))
                      .then(() => setCopy({ done: true, failed: false }))
                      .catch(() => setCopy({ done: false, failed: true }))
                  }}
                >
                  {language.t(copy.done ? "common.copied" : "sair.signIn.copy")}
                </Button>
              </div>
              <Show when={copy.failed}>
                <p role="status" class="sair-sign-in-copy-error">
                  {language.t("sair.signIn.copyFailed")}
                </p>
              </Show>
              <p class="text-center text-[13px] leading-[var(--line-height-base)] text-v2-text-text-muted">
                {language.t("sair.signIn.codeHint")}
              </p>
            </Show>
            <Show
              when={pendingRetry()}
              fallback={
                <Show when={!props.completing}>
                  <Button
                    variant="contrast"
                    size="large"
                    class="sair-sign-in-primary"
                    onClick={() => props.account?.reopen()}
                  >
                    <span>{language.t(props.web ? "sair.web.continue" : "sair.signIn.open")}</span>
                    <Icon name={props.web ? "arrow-right" : "arrow-up-right"} />
                  </Button>
                </Show>
              }
            >
              <Button
                variant="contrast"
                size="large"
                class="sair-sign-in-primary"
                disabled={props.account?.state.busy || props.account?.state.checking}
                onClick={() => void props.account?.retry()}
              >
                {language.t(props.account?.state.loadError ? "sair.retry" : "sair.access.recheck")}
              </Button>
            </Show>
            <div class="sair-sign-in-secondary">
              <Show when={pendingRetry()}>
                <Button variant="ghost" onClick={() => props.account?.reopen()}>
                  {language.t("sair.signIn.open")}
                </Button>
              </Show>
              <Button variant="ghost" disabled={props.account?.state.busy} onClick={() => void props.account?.cancel()}>
                {language.t("sair.signIn.cancel")}
              </Button>
            </div>
          </div>
        )}
      </Show>
      <Show when={canManageServers(platform)}>
        <details class="sair-sign-in-connection" open={!!unavailable()}>
          <summary>{language.t("sair.connection.settings")}</summary>
          <ServerChoice manage />
          <Show when={pending()}>
            {(attempt) => (
              <a
                class="break-all text-[13px] leading-[var(--line-height-base)] underline"
                href={attempt().verificationUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.preventDefault()
                  props.account?.reopen()
                }}
              >
                {attempt().verificationUrl}
              </a>
            )}
          </Show>
          <p class="text-[12px] leading-[var(--line-height-compact)] text-v2-text-text-faint">
            {language.t("sair.signIn.scope")}
          </p>
        </details>
      </Show>
    </section>
  )
}

export function SairAccountBar(props: { compact?: boolean; expanded?: boolean } = {}) {
  const language = useLanguage()
  const target = useSairTarget()
  const profile = () => target.account()?.state.status?.profile
  const [state, setState] = createStore({ failedAvatar: "" })
  if (props.compact) {
    const status = () => target.account()?.state.status?.model
    const pending = () => status() !== "ready"
    const message = () =>
      status() === "error"
        ? (target.account()?.state.status?.modelError?.message ?? language.t("sair.models.unavailable"))
        : language.t("sair.models.preparing")
    return (
      <div data-testid="sair-account" data-component="sair-account-compact" data-expanded={props.expanded || undefined}>
        <Popover placement="top-start" gutter={12}>
          <Popover.Trigger class="sair-account-trigger" aria-label={language.t("sair.account.menu")}>
            <span class="sair-account-avatar">
              <Show
                when={profile()?.avatarUrl && state.failedAvatar !== profile()?.avatarUrl}
                fallback={
                  <Avatar
                    size="small"
                    fallback={profile()?.name || profile()?.email || language.t("sair.account.menu")}
                  />
                }
              >
                <img
                  src={profile()?.avatarUrl}
                  alt={language.t("sair.account.avatar")}
                  onError={() => setState("failedAvatar", profile()?.avatarUrl ?? "")}
                />
              </Show>
              <Show when={pending() || target.account()?.state.loadError}>
                <span
                  class="sair-account-status"
                  data-error={status() === "error" || !!target.account()?.state.loadError}
                >
                  <span class="sr-only" role="status">
                    {target.account()?.state.loadError ? language.t("sair.connection.lost") : message()}
                  </span>
                </span>
              </Show>
            </span>
            <Show when={props.expanded}>
              <span class="sair-account-identity min-w-0">
                <span class="sair-account-name block truncate" dir="auto">
                  {profile()?.name || language.t("sair.account.menu")}
                </span>
              </span>
            </Show>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content data-component="sair-account-popover" class="sair-account-surface">
              <Popover.Title>{profile()?.name || language.t("sair.account.menu")}</Popover.Title>
              <p class="sair-account-email">{profile()?.email}</p>
              <ExternalLink class="sair-account-profile-link" href="https://id.sair.foundation/profile">
                {language.t("sair.account.profile")}
                <Icon name="arrow-up-right" size="small" />
              </ExternalLink>
              <ServerChoice />
              <Show when={target.account()?.state.loadError}>
                <p role="status">{language.t("sair.connection.lost")}</p>
              </Show>
              <Show when={pending()}>
                <div class="sair-account-models" role="status">
                  <span>{message()}</span>
                  <Show when={status() === "error"}>
                    <Button
                      variant="neutral"
                      size="small"
                      disabled={target.account()?.state.busy}
                      onClick={() => void target.account()?.retry()}
                    >
                      {language.t("sair.retry")}
                    </Button>
                  </Show>
                </div>
              </Show>
              <Show when={target.account()?.state.actionError}>
                <p role="alert">{language.t("sair.signIn.failed")}</p>
              </Show>
              <Button
                variant="neutral"
                disabled={target.account()?.state.busy}
                onClick={() => void target.account()?.logout()}
              >
                {language.t("sair.logout.action")}
              </Button>
            </Popover.Content>
          </Popover.Portal>
        </Popover>
      </div>
    )
  }
  return (
    <div
      class="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-v2-border-border-base px-4 py-2"
      data-testid="sair-account"
    >
      <ServerChoice />
      <Show when={target.account()?.state.loadError}>
        <p role="status" class="text-[12px] leading-[var(--line-height-compact)] text-v2-text-text-muted">
          {language.t("sair.connection.lost")}
        </p>
      </Show>
      <Show when={profile()}>
        {(user) => (
          <details class="relative ml-auto [app-region:no-drag]">
            <summary class="flex cursor-pointer list-none items-center gap-2 text-[12px] leading-[var(--line-height-compact)] text-v2-text-text-muted">
              <Show
                when={user().avatarUrl && state.failedAvatar !== user().avatarUrl}
                fallback={<Avatar size="small" fallback={user().name || user().email} />}
              >
                <img
                  class="size-6 rounded-full object-cover"
                  src={user().avatarUrl}
                  alt={language.t("sair.account.avatar")}
                  onError={() => setState("failedAvatar", user().avatarUrl)}
                />
              </Show>
              <span>{user().name || user().email}</span>
            </summary>
            <div class="sair-account-surface sair-account-surface-inline">
              <span class="break-all text-[13px] leading-[var(--line-height-base)] text-v2-text-text-base">
                {user().email}
              </span>
              <Show when={target.account()?.state.actionError}>
                <p role="alert">{language.t("sair.signIn.failed")}</p>
              </Show>
              <Button
                variant="neutral"
                disabled={target.account()?.state.busy}
                onClick={() => void target.account()?.logout()}
              >
                {language.t("sair.logout.action")}
              </Button>
            </div>
          </details>
        )}
      </Show>
      <Show when={target.account()?.state.status?.model !== "ready"}>
        <div
          class="flex w-full items-center gap-3 text-[12px] leading-[var(--line-height-compact)] text-v2-text-text-muted"
          role="status"
        >
          <Show when={target.account()?.state.status?.model === "unavailable"}>
            <span class="sair-check-spinner" aria-hidden="true" />
          </Show>
          <span>
            {target.account()?.state.status?.model === "error"
              ? (target.account()?.state.status?.modelError?.message ?? language.t("sair.models.unavailable"))
              : language.t("sair.models.preparing")}
          </span>
          <Show when={target.account()?.state.status?.model === "error"}>
            <Button
              variant="neutral"
              size="small"
              disabled={target.account()?.state.busy}
              onClick={() => void target.account()?.retry()}
            >
              {language.t("sair.retry")}
            </Button>
          </Show>
        </div>
      </Show>
    </div>
  )
}
